import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Handshake, Map } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const dur = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref} className="font-display text-5xl md:text-6xl font-semibold tracking-tight text-ecdeep tabular-nums">
      {val}
      <span className="text-ecamber">{suffix}</span>
    </span>
  );
};

export default function SuccessCases() {
  return (
    <section id="casos" className="relative py-24 md:py-36 bg-ecmist overflow-hidden" data-testid="success-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          num="10"
          chapter="Casos de éxito"
          id="casos"
          title={<>Resultados que hablan <span className="text-ecamber">por nosotros</span></>}
        />
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <Reveal>
            <div className="card-lift h-full bg-white border border-ecnavy/10 rounded-3xl p-10 soft-shadow" data-testid="success-stat-amazon">
              <Counter to={800} suffix="+" />
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                Pedidos gestionados en <span className="font-semibold text-ecdeep">Amazon</span> durante el último año.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-lift h-full bg-white border border-ecnavy/10 rounded-3xl p-10 soft-shadow" data-testid="success-stat-mercadolibre">
              <Counter to={77} suffix="+" />
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                Pedidos en <span className="font-semibold text-ecdeep">Mercado Libre</span> durante el último año.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="card-lift h-full bg-white border border-ecnavy/10 rounded-3xl p-10 soft-shadow" data-testid="success-stat-whatsapp">
              <Counter to={73} suffix="+" />
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                Ventas directas por <span className="font-semibold text-ecdeep">WhatsApp</span> con atención comercial personalizada.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="rounded-3xl bg-ecnavy p-10 text-white flex items-start gap-6"
            data-testid="success-card-distribuidores"
          >
            <span className="inline-flex h-14 w-14 shrink-0 rounded-2xl bg-ecamber text-ecdeep items-center justify-center">
              <Handshake size={26} strokeWidth={2} />
            </span>
            <div>
              <h3 className="font-display text-xl font-semibold">Distribuidores autorizados</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">
                Samsung, Syscom e Ingram Micro, con acceso directo a inventario y productos con garantía de fábrica.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="rounded-3xl bg-white border border-ecnavy/10 p-10 soft-shadow flex items-start gap-6"
            data-testid="success-card-cobertura"
          >
            <span className="inline-flex h-14 w-14 shrink-0 rounded-2xl bg-ecnavy text-white items-center justify-center">
              <Map size={26} strokeWidth={2} />
            </span>
            <div>
              <h3 className="font-display text-xl font-semibold text-ecdeep">Cobertura nacional</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Entregas en los 32 estados de la República Mexicana, con logística programada para cadenas.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
